import React, { useState } from "react";
import S from "./strings";
import Spectrum from "./Spectrum";
import { isRtlLanguage, monthYear } from "./locale";
import useUiLanguage from "./useUiLanguage";

// COACHING PERSONALITY — the private development comparison (WP2 decision).
//
// "After a retake the coach ALONE can open a previous-versus-current view of
// the five markers, minimal, no export." So:
//   * it is drawn from GET /users/coaching-personality/history/, an owner only
//     endpoint (coaching_personality_views.py:431). Nothing about it exists on
//     the club side card, in a list, or anywhere else,
//   * it appears only when there IS a second result to compare against. One
//     result renders nothing, not an empty state,
//   * it is collapsed by default. A portrait is not a scoreboard, and the
//     comparison is the one place this feature could start to read like one,
//   * no export, no download, no share.
//
// Konzept 6 keeps CLUB side comparison (two people side by side) out of this
// build entirely. This is the other thing: one person, two dates.
//
// Every word in it is a registered sentence: the two dates ride the "As of
// {date}" template and the two readings are the catalogue's own short labels.
// Only the heading and the one explaining line are new copy.

const Legend = ({ hollow, label }) => (
  <span className="inline-flex items-center gap-2 min-w-0">
    <span
      className="w-[9px] h-[9px] rounded-full flex-none"
      style={{
        background: hollow ? "#FFFFFF" : "#2183FE",
        border: "2px solid #2183FE",
        boxSizing: "content-box",
      }}
    />
    <span className="min-w-0">{label}</span>
  </span>
);

const Development = ({ results }) => {
  const language = useUiLanguage();
  const rtl = isRtlLanguage(language);
  const [open, setOpen] = useState(false);

  const list = Array.isArray(results) ? results : [];
  if (list.length < 2) return null;

  const previous = list[list.length - 2];
  const current = list[list.length - 1];
  const previousAxes = previous?.axes || [];
  const currentAxes = current?.axes || [];
  if (!previousAxes.length || previousAxes.length !== currentAxes.length) return null;

  const previousDate = monthYear(previous.completed_at, language);
  const currentDate = monthYear(current.completed_at, language);

  return (
    <div
      dir={rtl ? "rtl" : undefined}
      className="mt-4 border border-[#E5E7EB] rounded-[8px]"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 text-start px-5 py-4 bg-transparent border-none"
      >
        <span className="text-[15px] font-bold text-[#111827] min-w-0">
          {S.developmentTitle}
        </span>
        <span className="flex-1" />
        <span
          aria-hidden="true"
          className={`flex-none text-[#6B7280] text-[12px] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          &#9660;
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5">
          <p className="text-[13px] leading-[1.5] text-[#6B7280]">
            {S.developmentExplainer}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-[#6B7280]">
            <Legend hollow label={S.asOf(previousDate)} />
            <Legend label={S.asOf(currentDate)} />
          </div>
          <div className="mt-4 flex flex-col gap-[18px]">
            {currentAxes.map((axis, position) => {
              const before = previousAxes[position];
              return (
                <div key={axis.axis}>
                  <div className="text-[13px] font-semibold text-[#111827] min-w-0">
                    {axis.name}
                  </div>
                  <Spectrum
                    position={axis.marker_position}
                    previousPosition={before?.marker_position}
                    size="inner"
                    rtl={rtl}
                  />
                  <div className="mt-[10px] flex flex-col gap-1 text-[12px] text-[#6B7280]">
                    <Legend hollow label={before?.label} />
                    <Legend label={axis.label} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Development;
